import { useMemo } from "react";
import type { MetricUsage, UsageResponse } from "@/app/billing/types";
import { toFriendlyMetricName } from "@/app/billing/utils";

type ChartDataPoint = {
  date: string;
  value: number;
  [key: string]: string | number;
};

export type ChartConfig = {
  title: string;
  description?: string;
  data: ChartDataPoint[];
  chartType: "bar" | "line";
  dataKey: string;
  usageMetric: MetricUsage;
};

export const useUsageCharts = (usageData?: UsageResponse): ChartConfig[] =>
  useMemo(() => {
    if (!Array.isArray(usageData) || usageData.length === 0) {
      return [];
    }

    const toFriendlyName = (rawName: string): string =>
      toFriendlyMetricName(rawName);

    return usageData
      .map((metric, index) => {
        const chartData = metric.usage.map((sample) => ({
          date: sample.timeframe_end.split("T")[0],
          value: sample.quantity,
        }));

        return {
          title: toFriendlyName(metric.billable_metric.name),
          data: chartData,
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          chartType: (index % 2 === 0 ? "bar" : "line") as "bar" | "line",
          dataKey: "value",
          usageMetric: metric,
        };
      })
      .filter((config) => config.data.some((d) => Number(d.value) > 0));
  }, [usageData]);
